import type { AlpineComponent } from 'alpinejs';

type TabsData = {
  tabs: NodeListOf<HTMLElement> | [];
  panels: NodeListOf<HTMLElement> | [];
  activeTab?: HTMLElement;
  activeTabClientRect?: DOMRect;
  gliderStyle: Record<string, any> | null;
  activateTab: (tab: HTMLElement) => void;
  updateDimensions: () => void;
  bindControlEvents: () => void;
  determineOrientation: (event: KeyboardEvent, tabIndex: number) => void;
  switchTabOnArrowPress: (key: string, tabIndex: number) => void;
};

const Tabs = (): AlpineComponent<TabsData> => ({
  tabs: [],

  panels: [],

  activeTab: undefined,

  activeTabClientRect: undefined,

  get gliderStyle() {
    if (!this.activeTabClientRect) {
      return null;
    }

    const { x, width, height } = this.activeTabClientRect;
    const { scrollLeft, offsetLeft } = this.$refs.list;

    return {
      '--x': `${x + scrollLeft - offsetLeft}px`,
      '--width': `${width}px`,
      '--height': `${height}px`
    };
  },

  activateTab(tab: HTMLElement) {
    this.activeTab = tab;
    this.activeTabClientRect = tab.getBoundingClientRect();

    tab.scrollIntoView({
      block: 'nearest',
      inline: 'center',
      behavior: 'smooth'
    });
  },

  updateDimensions() {
    this.activeTabClientRect = this.activeTab?.getBoundingClientRect();
  },

  bindControlEvents() {
    this.tabs.forEach((tab: HTMLElement, tabIndex: number) => {
      tab.addEventListener('click', (event) => {
        event.preventDefault();

        if (event.currentTarget instanceof HTMLElement) {
          this.activateTab(event.currentTarget);
        }
      });

      // Handle keydown on tabs
      tab.addEventListener('keydown', (event) => {
        event.preventDefault();

        const firstTab = this.tabs[0];
        const lastTab = this.tabs[this.tabs.length - 1];

        switch (event.key) {
          // When focus is on an tab, moves focus to the last tab.
          case 'End': {
            event.preventDefault();

            lastTab.focus();
            this.activateTab(lastTab);
            break;
          }
          // When focus is on an tab, moves focus to the first tab.
          case 'Home': {
            event.preventDefault();

            firstTab.focus();
            this.activateTab(firstTab);
            break;
          }
          // Up and down are in keydown
          // because we need to prevent page scroll >:)
          case 'ArrowUp':
          case 'ArrowDown': {
            this.determineOrientation(event, tabIndex);
            break;
          }
          default: {
            break;
          }
        }
      });

      // Handle keyup on tabs
      tab.addEventListener('keyup', (event) => {
        event.preventDefault();

        switch (event.key) {
          case 'ArrowRight':
          case 'ArrowLeft': {
            this.determineOrientation(event, tabIndex);
            break;
          }
          default: {
            break;
          }
        }
      });
    });
  },

  // When a lists aria-orientation is set to vertical,
  // only up and down arrow should function.
  // In all other cases only left and right arrow function.
  determineOrientation(event: KeyboardEvent, tabIndex: number) {
    const { key } = event;
    const vertical =
      this.$refs.list.getAttribute('aria-orientation') === 'vertical';

    if (vertical) {
      if (key === 'ArrowUp' || key === 'ArrowDown') {
        event.preventDefault();
        this.switchTabOnArrowPress(key, tabIndex);
      }
    } else if (key === 'ArrowLeft' || key === 'ArrowRight') {
      event.preventDefault();
      this.switchTabOnArrowPress(key, tabIndex);
    }
  },

  // Either focus the next, previous, first, or last tab
  // depening on key pressed
  switchTabOnArrowPress(key: string, tabIndex: number) {
    const tabCount = this.tabs.length;

    switch (key) {
      case 'ArrowUp':
      case 'ArrowRight': {
        const newIndex = (tabIndex + tabCount + 1) % tabCount;
        const nextTab = this.tabs[newIndex];
        nextTab.focus();
        this.activateTab(nextTab);
        break;
      }
      case 'ArrowDown':
      case 'ArrowLeft': {
        const newIndex = (tabIndex + tabCount - 1) % tabCount;
        const previousTab = this.tabs[newIndex];
        previousTab.focus();
        this.activateTab(previousTab);
        break;
      }
      default: {
        break;
      }
    }
  },

  init() {
    this.tabs = this.$root.querySelectorAll('.tabs__tab');
    this.panels = this.$root.querySelectorAll('.tabs__panel');

    this.activeTab = this.tabs[0];

    this.bindControlEvents();
  }
});

export default Tabs;
